import {
  IJitFeedFacilityStatus,
  IJitFeedHoldInput,
  IMarketplaceInventoryInput,
  IVendorInventoryUploadInput,
} from '@admin-panel/types';
import CrudApiClient from './crudapiclient';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { CrudFilters, CrudSort, MetaQuery } from '@refinedev/core';
import { axiosInstance } from './axios';

class InventoryApiClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/inventory');
  }

  override async createMany<T = unknown>(
    payload: Array<Partial<T>>,
    meta: MetaQuery | undefined,
  ): Promise<Array<T>> {
    try {
      const uploadPayload = payload as unknown as Array<IVendorInventoryUploadInput>;
      const invRows: Array<IMarketplaceInventoryInput> = uploadPayload.map(
        (inv: IVendorInventoryUploadInput) => {
          const rawQuantity =
            inv.updatedInventory !== undefined && String(inv.updatedInventory).trim() !== ''
              ? inv.updatedInventory
              : inv.warehouseInventory;
          const quantity = Number(rawQuantity);
          if (rawQuantity === null || rawQuantity === undefined || String(rawQuantity).trim() === '' ||
              !Number.isInteger(quantity) || quantity < 0) {
            throw new Error('Warehouse Inventory must be a non-negative integer');
          }
          const baseData = {
            quantity,
            vendorId: meta?.vendorId,
            facilityCode: meta?.unicomFacilityCode,
          };

          if (meta?.useVendorSkuCode) {
            return {
              ...baseData,
              vendorSkuCode: inv.vendorSkuCode || inv.skuCode,
            };
          } else {
            return {
              ...baseData,
              skuCode: inv.skuCode,
            };
          }
        },
      );

      const response = await axiosInstance.post(`${this.baseURL}/upload`, invRows);
      if (response.data?.success === false) {
        throw new Error(response.data?.status?.message || 'Inventory upload was rejected');
      }
      // Response rows use quantity; the editable CSV uses Warehouse Inventory.
      // Preserve both on accepted and rejected rows so exporting a correction is safe.
      const summaries = response.data?.data;
      const text = (value: unknown) => String(value ?? '').trim();
      if (response.data?.success !== true || !Array.isArray(summaries) || summaries.length !== invRows.length ||
          summaries.some((summary, index) =>
            !summary?.input || summary.input.quantity !== invRows[index].quantity ||
            text(summary.input.skuCode) !== text(invRows[index].skuCode) ||
            text(summary.input.vendorSkuCode) !== text(invRows[index].vendorSkuCode) ||
            text(summary.input.vendorId) !== text(invRows[index].vendorId) ||
            text(summary.input.facilityCode).toLowerCase() !== text(invRows[index].facilityCode).toLowerCase() ||
            !['CREATE', 'FAILED'].includes(summary.summary?.operation) ||
            !Array.isArray(summary.remarks) || summary.remarks.length === 0 ||
            summary.remarks.some((remark: unknown) => typeof remark !== 'string' || !remark.trim()))) {
        throw new Error('Inventory upload returned incomplete results. Refresh inventory before retrying');
      }
      return this.transformList(
        summaries.map(summary => ({
          ...summary,
          input: {
            ...summary.input,
            warehouseInventory: summary.input?.quantity,
          },
        })),
      ) as Array<T>;
    } catch (error) {
      console.error(`Error uploading entities in bulk:`, error);
      throw error;
    }
  }

  async getOpenBlocks(
    _sorters: Array<CrudSort>,
    _filters: CrudFilters,
    _payload: unknown,
    _query: unknown,
    _headers: unknown,
    meta: MetaQuery | undefined,
  ): Promise<unknown> {
    if (!meta?.inventoryId) {
      throw new Error('inventoryId is required to load blocks');
    }
    const response = await axiosInstance.get(`${this.baseURL}/${meta.inventoryId}/blocks`);
    if (response.data?.success === false) {
      throw new Error(response.data?.status?.message || 'Failed to load blocks');
    }
    return this.transformList(response.data.data ?? []);
  }

  /** JIT stale-feed nudge status of every facility of one vendor (Foundation vendor page). */
  async getJitFeedFacilities(
    _sorters: Array<CrudSort>,
    _filters: CrudFilters,
    _payload: unknown,
    _query: unknown,
    _headers: unknown,
    meta: MetaQuery | undefined,
  ): Promise<Array<IJitFeedFacilityStatus>> {
    if (meta?.vendorId == null) {
      throw new Error('vendorId is required to load the inventory feed');
    }
    const response = await axiosInstance.get(`${this.baseURL}/jit-feed/facilities`, {
      params: { vendorId: meta.vendorId },
    });
    if (response.data?.success === false) {
      throw new Error(response.data?.status?.message || 'Failed to load the inventory feed');
    }
    return this.transformList(response.data?.data ?? []);
  }

  /** Hold the delist of a facility's open stale-feed episode until an IST date. */
  async setJitFeedHold(
    _sorters: Array<CrudSort>,
    _filters: CrudFilters,
    payload: IJitFeedHoldInput,
    _query: unknown,
    _headers: unknown,
    meta: MetaQuery | undefined,
  ): Promise<unknown> {
    if (!meta?.facilityCode) {
      throw new Error('facilityCode is required to hold a delist');
    }
    const response = await axiosInstance.put(
      `${this.baseURL}/jit-feed/facilities/${encodeURIComponent(meta.facilityCode)}/hold`,
      payload,
    );
    if (response.data?.success === false) {
      throw new Error(response.data?.status?.message || 'Failed to hold the delist');
    }
    return response.data?.data;
  }

  async clearJitFeedHold(
    _sorters: Array<CrudSort>,
    _filters: CrudFilters,
    _payload: unknown,
    _query: unknown,
    _headers: unknown,
    meta: MetaQuery | undefined,
  ): Promise<unknown> {
    if (!meta?.facilityCode) {
      throw new Error('facilityCode is required to clear a hold');
    }
    const response = await axiosInstance.delete(
      `${this.baseURL}/jit-feed/facilities/${encodeURIComponent(meta.facilityCode)}/hold`,
    );
    if (response.data?.success === false) {
      throw new Error(response.data?.status?.message || 'Failed to clear the hold');
    }
    return response.data?.data;
  }

  async updateWarehouseInventory(
    _sorters: Array<CrudSort>,
    _filters: CrudFilters,
    payload: { warehouseInventory: number },
    _query: unknown,
    _headers: unknown,
    meta: MetaQuery | undefined,
  ): Promise<unknown> {
    try {
      if (!meta?.inventoryId) {
        throw new Error('inventoryId is required to update warehouse inventory');
      }
      const response = await axiosInstance.put(
        `${this.baseURL}/${meta?.inventoryId}/warehouse-inventory`,
        payload,
      );
      if (response.data?.success === false) {
        throw new Error(response.data?.status?.message || 'Failed to update warehouse inventory');
      }
      return this.transformNode(response.data.data);
    } catch (error) {
      console.error(`Error updating warehouse inventory:`, error);
      throw error;
    }
  }
}

export default InventoryApiClient;
